import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, refreshUser } = useAuth();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Processing authentication...');

  useEffect(() => {
    // CRITICAL: Prevent duplicate processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Authentication failed - no session');
          navigate('/login');
          return;
        }

        setStatus('Validating session...');

        // Exchange session_id for user data and set cookie
        const response = await axios.post(
          `${API}/auth/session?session_id=${sessionId}`,
          {},
          { withCredentials: true }
        );

        const userData = response.data;
        
        setStatus('Fetching user profile...');

        // CRITICAL: Fetch full user data with roles from /me endpoint
        // This ensures the cookie is set and we get complete user info
        const meResponse = await axios.get(`${API}/auth/me`, {
          withCredentials: true
        });

        const fullUser = meResponse.data;
        
        setStatus('Initializing session...');

        // Update auth context with full user data
        await login(fullUser);

        // Small delay to ensure state propagation
        await new Promise(resolve => setTimeout(resolve, 100));

        setStatus('Redirecting to dashboard...');

        // Navigate to dashboard
        navigate('/dashboard', { replace: true });
        
      } catch (error) {
        console.error('Auth error:', error);
        toast.error('Authentication failed. Please try again.');
        navigate('/login');
      }
    };

    processSession();
  }, [navigate, location.hash, login, refreshUser]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
